// src/app/core/interceptors/server-error.interceptor.ts
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Observable, throwError, timer, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ServerErrorService } from '../services/server-error.service';
import { Library } from '../library';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
    private readonly API_DOMAIN = Library.API_DOMAIN;
    private isVerifying = false; // Prevent multiple verifications

    constructor(
        private router: Router,
        private serverErrorService: ServerErrorService,
        private http: HttpClient
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                // ✅ ONLY handle true network/domain unreachable errors
                if (this.isDomainUnreachable(error, request.url)) {
                    console.log('Potential domain unreachable detected - verifying...');

                    // ✅ Verify server is actually down before showing error page
                    return from(this.verifyServerDown()).pipe(
                        switchMap(isServerDown => {
                            if (isServerDown && !this.isVerifying) {
                                console.log('Server confirmed down - showing error page');

                                const currentUrl = this.router.url;
                                this.serverErrorService.setServerError(true, currentUrl);
                                this.router.navigate(['/server-error']);
                            } else {
                                console.log('Server is reachable - false alarm');
                            }

                            return throwError(() => error);
                        })
                    );
                }

                // Always pass the error through
                return throwError(() => error);
            })
        );
    }

    /**
     * ✅ Verify the server is actually down by attempting a lightweight ping
     */
    private async verifyServerDown(): Promise<boolean> {
        if (this.isVerifying) {
            return false;
        }

        this.isVerifying = true;

        // ✅ Try 2 quick pings before giving up
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const pingResult = await this.pingServer(2000); // 2 second timeout

                if (pingResult) {
                    this.isVerifying = false;
                    console.log(`Ping attempt ${attempt + 1}: Server is UP`);
                    return false; // Server is UP
                }
            } catch (error) {
                console.log(`Ping attempt ${attempt + 1}: Failed`, error);
                if (attempt < 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        }

        this.isVerifying = false;
        console.log('All ping attempts failed - server is DOWN');
        return true; // Server is DOWN
    }

    /**
     * Helper method to ping server with timeout
     */
    private pingServer(timeoutMs: number): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                controller.abort();
                reject(new Error('Timeout'));
            }, timeoutMs);

            fetch(this.API_DOMAIN, {
                method: 'HEAD',
                mode: 'no-cors',
                cache: 'no-cache',
                signal: controller.signal
            })
                .then(() => {
                    clearTimeout(timeoutId);
                    resolve(true);
                })
                .catch((error) => {
                    clearTimeout(timeoutId);
                    reject(error);
                });
        });
    }

    /**
     * ✅ Check if the domain/server is completely unreachable
     */
    private isDomainUnreachable(error: HttpErrorResponse, requestUrl: string): boolean {
        // Only check requests to our API domain
        if (!requestUrl.includes(this.API_DOMAIN.replace('https://', '').replace('http://', ''))) {
            return false;
        }

        // ✅ Status 0 = Network error (no HTTP response received)
        if (error.status === 0) {
            const isNetworkError =
                error.error instanceof ProgressEvent ||
                error.message.includes('ERR_CONNECTION_REFUSED') ||
                error.message.includes('ERR_CONNECTION_TIMED_OUT') ||
                error.message.includes('ERR_NAME_NOT_RESOLVED') ||
                error.message.includes('ERR_INTERNET_DISCONNECTED') ||
                error.message.includes('net::ERR_') ||
                error.message.includes('Failed to fetch') ||
                error.message.includes('NetworkError') ||
                error.message.includes('Network request failed');

            if (isNetworkError) {
                console.log('Network error detected:', error.message);
                return true;
            }
        }

        return false;
    }
}